import DOMPurify from 'dompurify';
import {
  BRANDID, COMPANY_CONTACT_UPDATE_API,
  UPDATE_CONTACT_API,
} from './constants';
import { getCookieValue, setCookie } from './cookie';
import {
  isLeadInfoValid, setPhoneFlagAttribute,
  showPlansFold, removeLeadRegistrationFold, makeLeadRegistrationRequest,
  getRefinedLeadInfo, highlightInvalidFields,
} from '../util/registerUtil';
import { isTapNativeCookiePresent } from './tapNativeTracker';

const isRegistered = getCookieValue('isregistered') === 'true';
const isDemoRegistered = getCookieValue('isDemoRegistered') === 'true';
const demoNumBtn = document.querySelector('#demo-register-btn');
const updatedDemoNumberField = document.querySelector('#demo-number-update');
const isFromJoepopPage = document.referrer.includes('/joe') || window.location.pathname.includes('/joe');
const isFromStartupNationPage = document.referrer.includes('/startupnation') || window.location.pathname.includes('/startupnation');
const isFromIgniteDDSPage = document.referrer.includes('/ignitedds') || window.location.pathname.includes('/ignitedds');
const isFromMcCancePage = document.referrer.includes('/mccance') || window.location.pathname.includes('/mccance');
const isFromDentalpreneurPage = document.referrer.includes('/dentalpreneur') || window.location.pathname.includes('/dentalpreneur');
const registerLpForm = document.querySelector('.register-lp');
let doesCompanyContactExist = !!getCookieValue('companyContactId');
let leadInfo = {};
const fiftyPercentOfftillThreePlanPaths = ['/aaos', '/joe', '/startupnation', '/ignitedds', '/dentalpreneur'];

function showDemoNumber(demoNumber = '') {
  updatedDemoNumberField.setAttribute('href', `tel:+1${demoNumber}`);
  updatedDemoNumberField.textContent = `${demoNumber.substring(
    0,
    3,
  )}-${demoNumber.substring(3, 6)}-${demoNumber.substring(6)}`;
  document.getElementById('demoFold').classList.add('demo-registered');
}

function updateAndShowDemoNumber(demoNumber = '') {
  setCookie('isDemoRegistered', true, 1);
  setCookie('demoNumber', demoNumber, 1);
  showDemoNumber(demoNumber);
}

const leadRegistrationSuccessCallback = (response) => {
  const referrerUrl = new window.URL(
    document.referrer || window.location.href,
  );
  const applyFiftyPercentOfftillThreePlan = fiftyPercentOfftillThreePlanPaths
    .some(path => referrerUrl.pathname.startsWith(path));

  if (response.companyContactId) {
    setCookie('companyContactId', response.companyContactId, 365);
    doesCompanyContactExist = true;
  }
  updateAndShowDemoNumber(response.demoNumber);
  if (registerLpForm) {
    registerLpForm.classList.add('is-registered');
  }
  if (referrerUrl && (referrerUrl.pathname.startsWith('/lp/smilecon') || referrerUrl.pathname.startsWith('/lp/mhm'))) {
    setCookie('fiftyPercentOff', true, 365);
  }
  if (referrerUrl && (referrerUrl.pathname.startsWith('/lp/aaos-annual-meeting') || referrerUrl.pathname.startsWith('/lp/tda-meeting'))) {
    setCookie('thirtyPercentOff', true, 365);
  }
  if (referrerUrl && applyFiftyPercentOfftillThreePlan) {
    setCookie('tillThreePlan', true, 365);
  }
  showPlansFold(getCookieValue('uniquePin'));
  // Plans are not rendered for unregistered users, so drop the registration fold instead;
  // the server renders plans in its place on the next page load
  if (!document.querySelector('#plans-wrapper')) removeLeadRegistrationFold();
};

function setDemoFoldToNormal() {
  demoNumBtn.classList.remove('processing');
}

const leadRegistrationfailureCallback = () => {
  setDemoFoldToNormal();
};

function onBlur(event, info) {
  if (info.valid && DOMPurify.sanitize(info.value) === info.value) {
    document.querySelector(info.selector).classList.add('is-valid');
  } else {
    // eslint-disable-next-line no-param-reassign
    info.valid = false;
    document.querySelector(info.selector).classList.remove('is-valid');
    document.querySelector(info.selector).classList.add('is-err');
  }
  leadInfo[info.emittedBy] = info;

  const elementFocused = event.target;
  if (elementFocused.id === 'demo-phone') {
    const phoneValidElem = document.getElementById('demo-phone-valid');
    const phoneInValidElem = document.getElementById('demo-phone-invalid');
    if (elementFocused.classList.contains('is-err')) {
      phoneInValidElem.setAttribute('style', 'display:block;');
      phoneValidElem.setAttribute('style', 'display:none;');
    } else {
      phoneInValidElem.setAttribute('style', 'display:none;');
      phoneValidElem.setAttribute('style', 'display:block;');
    }
  }
}

function onFocus(event) {
  if (event.target.classList.contains('is-valid')) {
    event.target.classList.remove('is-valid');
  }
  if (event.target.classList.contains('is-err')) {
    event.target.classList.remove('is-err');
  }
  const elementFocused = event.target;
  if (elementFocused.id === 'demo-phone') {
    const phoneValidElem = document.getElementById('demo-phone-valid');
    const phoneInValidElem = document.getElementById('demo-phone-invalid');
    phoneInValidElem.setAttribute('style', 'display:none;');
    phoneValidElem.setAttribute('style', 'display:none;');
  }
}

leadInfo = window.InputValidator && window.InputValidator.fields.initialize({
  fields: {
    ...(!isDemoRegistered && {
      company: {
        selector: '#demo-practiceName',
        type: 'text',
        errorClassName: 'is-err',
      },
      ...(!isRegistered && {
        phone: {
          selector: '#demo-phone',
          type: 'phone',
          withDialCodePicker: true,
          country: (window.siteInfo && window.siteInfo.country) || 'US',
          errorClassName: 'is-err',
        },
        mail: {
          selector: '#demo-email',
          type: 'email',
          errorClassName: 'is-err',
        },
        name: {
          selector: '#demo-name',
          type: 'text',
          errorClassName: 'is-err',
        },
      }),
    }),
  },
  eventListeners: {
    blur: onBlur,
    focus: onFocus,
  },
});

function getLeadRegistrationPayload() {
  return {
    ...getRefinedLeadInfo(leadInfo),
    brandid: BRANDID,
    cookie: document.cookie,
    referrer: document.baseURI,
    timezone: window.getUserTimezone && window.getUserTimezone(),
    ...(isTapNativeCookiePresent() && { leadSource: ' - TapNative' }),
    ...(isFromJoepopPage && { leadSource: ' - POTP' }),
    ...(isFromStartupNationPage && { leadSource: ' - Private practice startup' }),
    ...(isFromIgniteDDSPage && { leadSource: ' - Ignite DDS' }),
    ...(isFromMcCancePage && { leadSource: ' - McCance' }),
    ...(isFromDentalpreneurPage && { leadSource: ' - Dentalpreneur' }),
  };
}

async function makeCompanyCreateRequest(payload) {
  try {
    let response = await window.fetch(UPDATE_CONTACT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    response = await response.json();
    if (response && response.status === 'success') {
      updateAndShowDemoNumber(response.demoNumber);
      setCookie('companyContactId', response.companyContactId, 365);
      doesCompanyContactExist = true;
    } else {
      setDemoFoldToNormal();
    }
  } catch (e) {
    setDemoFoldToNormal();
    console.error(e); //eslint-disable-line
  }
}

async function makeCompanyUpdateRequest(payload) {
  try {
    let response = await window.fetch(COMPANY_CONTACT_UPDATE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    response = await response.json();
    if (response && response.status === 'success') {
      updateAndShowDemoNumber(response.demoNumber);
    }
    setDemoFoldToNormal();
  } catch (e) {
    setDemoFoldToNormal();
    console.error(e); //eslint-disable-line
  }
}

function demoCreationHandler() {
  if (isLeadInfoValid(leadInfo)) {
    demoNumBtn.classList.add('processing');

    if (!isRegistered) {
      makeLeadRegistrationRequest(
        {
          ...getLeadRegistrationPayload(),
          event: 'DEMO_LEAD',
        },
        leadRegistrationSuccessCallback,
        leadRegistrationfailureCallback,
      );
    } else if (!doesCompanyContactExist) {
      makeCompanyCreateRequest({
        contactId: getCookieValue('uniquePin'),
        fieldsToUpdate: {
          linkedContacts: {
            companyName: leadInfo.company.value,
          },
        },
      });
    } else {
      makeCompanyUpdateRequest({
        contactId: getCookieValue('uniquePin'),
        companyName: leadInfo.company.value,
        companyContactId: getCookieValue('companyContactId'),
      });
    }
  } else {
    highlightInvalidFields(leadInfo);
  }
}

if (isDemoRegistered) {
  showDemoNumber(getCookieValue('demoNumber'));
  if (registerLpForm) {
    registerLpForm.classList.add('is-registered');
  }
  showPlansFold(getCookieValue('uniquePin'));
} else if (isRegistered) {
  document.getElementById('demo-registration').classList.add('is-registered');
  if (registerLpForm) {
    registerLpForm.classList.add('is-registered');
  }
  showPlansFold(getCookieValue('uniquePin'));
  if (demoNumBtn) {
    demoNumBtn.addEventListener('click', demoCreationHandler);
  }
} else if (demoNumBtn) {
  demoNumBtn.addEventListener('click', demoCreationHandler);
}

if (!isDemoRegistered) {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });
}

window.addEventListener('load', () => {
  setPhoneFlagAttribute();
});
